/**
 * KPI Dashboard Page
 * Displays OEE, Lead-Time, Utilization, and OTD metrics
 * with trend charts and snapshot management.
 */

import { useState, useMemo } from 'react';
import { Layout } from '../components/layout';
import {
  useKPIOee,
  useKPILeadTime,
  useKPIUtilization,
  useKPIOtd,
  useKPISnapshots,
  useKPITrend,
  useCreateKPISnapshot,
  getOEEColor,
  getOTDColor,
  getUtilizationColor,
  getKPIMetricLabel,
  formatPercent,
} from '../hooks';
import type {
  KPIRequest,
  KPIMetric,
  KPISnapshotListItem,
} from '../types';
import {
  Activity,
  Clock,
  Gauge,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Camera,
  Loader2,
  RefreshCw,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Minus,
} from 'lucide-react';
import clsx from 'clsx';

// ============ Date helpers ============

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getDefaultDateRange(): { date_from: string; date_to: string } {
  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - 30);
  return {
    date_from: from.toISOString().split('T')[0],
    date_to: now.toISOString().split('T')[0],
  };
}

// ============ KPI Card Component ============

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  colorClass: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  loading?: boolean;
}

function KPICard({
  title,
  value,
  subtitle,
  icon: Icon,
  colorClass,
  trend,
  trendValue,
  loading,
}: KPICardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div
          className={clsx(
            'w-12 h-12 rounded-lg flex items-center justify-center',
            colorClass === 'text-green-600'
              ? 'bg-green-50'
              : colorClass === 'text-amber-600'
              ? 'bg-amber-50'
              : colorClass === 'text-red-600'
              ? 'bg-red-50'
              : 'bg-blue-50'
          )}
        >
          <Icon className={clsx('w-6 h-6', colorClass)} />
        </div>
        {trend && (
          <div
            className={clsx(
              'flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full',
              trend === 'up'
                ? 'bg-green-50 text-green-600'
                : trend === 'down'
                ? 'bg-red-50 text-red-600'
                : 'bg-gray-50 text-gray-500'
            )}
          >
            {trend === 'up' ? (
              <TrendingUp className="w-3 h-3" />
            ) : trend === 'down' ? (
              <TrendingDown className="w-3 h-3" />
            ) : (
              <Minus className="w-3 h-3" />
            )}
            {trendValue}
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-sm text-gray-500">{title}</p>
        {loading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            <span className="text-gray-400 text-sm">Calculating...</span>
          </div>
        ) : (
          <>
            <p className={clsx('text-2xl font-bold', colorClass)}>{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-400">{subtitle}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============ Utilization Bar ============

function UtilizationBar({
  label,
  value,
  maxValue = 100,
}: {
  label: string;
  value: number;
  maxValue?: number;
}) {
  const pct = Math.min((value / maxValue) * 100, 100);
  const overloaded = value > maxValue;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600 truncate">{label}</span>
        <span
          className={clsx(
            'font-medium',
            getUtilizationColor(value)
          )}
        >
          {formatPercent(value)}
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full transition-all duration-500',
            overloaded
              ? 'bg-red-500'
              : value >= 80
              ? 'bg-green-500'
              : value >= 50
              ? 'bg-amber-400'
              : 'bg-gray-300'
          )}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

// ============ Snapshot List ============

function SnapshotList({
  snapshots,
  loading,
}: {
  snapshots: KPISnapshotListItem[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <p className="text-center text-gray-400 py-8 text-sm">
        No snapshots available yet
      </p>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {snapshots.slice(0, 10).map((snap) => (
        <div
          key={snap.id}
          className="flex items-center justify-between py-3 px-1"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">
              {snap.snapshot_type === 'daily'
                ? 'Daily Snapshot'
                : snap.snapshot_type === 'weekly'
                ? 'Weekly Snapshot'
                : snap.snapshot_type === 'monthly'
                ? 'Monthly Snapshot'
                : 'Manual'}
            </p>
            <p className="text-xs text-gray-400">
              {formatDate(snap.snapshot_date)}
            </p>
          </div>
          <div className="text-right">
            <p className={clsx('text-sm font-semibold', getOEEColor(Number(snap.oee_overall ?? 0) * 100))}>
              OEE {formatPercent(Number(snap.oee_overall ?? 0) * 100)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ Main Page ============

export function KPIDashboardPage() {
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [selectedMetric, setSelectedMetric] = useState<KPIMetric>('oee_overall');

  const kpiRequest: KPIRequest = useMemo(
    () => ({
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
    }),
    [dateRange]
  );

  // KPI data queries
  const oee = useKPIOee(kpiRequest);
  const leadTime = useKPILeadTime(kpiRequest);
  const utilization = useKPIUtilization(kpiRequest);
  const otd = useKPIOtd(kpiRequest);
  const snapshots = useKPISnapshots();
  const trend = useKPITrend(selectedMetric, { days: 30 });
  const createSnapshot = useCreateKPISnapshot();

  // Derived values — map to actual backend response structure
  const oeeValue = Number(oee.data?.summary?.avg_oee ?? 0) * 100; // API returns 0-1, display as %
  const otdValue = Number(otd.data?.otd_rate ?? 0) * 100;
  const avgUtil = Number(utilization.data?.summary?.avg_utilization ?? 0) * 100;
  const avgLeadTime = Number(leadTime.data?.avg_hours ?? 0);

  const trendMetrics: { key: KPIMetric; label: string }[] = [
    { key: 'oee_overall', label: 'OEE Overall' },
    { key: 'otd_rate', label: 'On-Time Delivery' },
    { key: 'avg_wc_utilization', label: 'Utilization' },
    { key: 'avg_lead_time_hours', label: 'Lead Time' },
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-[#45bdd6]" />
              KPI Dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Production metrics and trend analysis
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Date Range */}
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={dateRange.date_from}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    date_from: e.target.value,
                  }))
                }
                className="text-sm border-none focus:outline-none bg-transparent"
              />
              <span className="text-gray-300">—</span>
              <input
                type="date"
                value={dateRange.date_to}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    date_to: e.target.value,
                  }))
                }
                className="text-sm border-none focus:outline-none bg-transparent"
              />
            </div>
            {/* Snapshot Button */}
            <button
              onClick={() =>
                createSnapshot.mutate({
                  snapshot_date: new Date().toISOString().split('T')[0],
                  snapshot_type: 'daily',
                })
              }
              disabled={createSnapshot.isPending}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                createSnapshot.isPending
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-[#45bdd6] text-white hover:bg-[#3ba8c0]'
              )}
            >
              {createSnapshot.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
              Snapshot
            </button>
            {/* Refresh */}
            <button
              onClick={() => {
                oee.refetch();
                leadTime.refetch();
                utilization.refetch();
                otd.refetch();
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* KPI Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="OEE Overall"
            value={oee.isLoading ? '—' : formatPercent(oeeValue)}
            subtitle={
              oee.data?.summary
                ? `${oee.data.summary.work_centers_analyzed} work centers analyzed`
                : undefined
            }
            icon={Gauge}
            colorClass={oee.isLoading ? 'text-gray-400' : getOEEColor(oeeValue)}
            loading={oee.isLoading}
          />
          <KPICard
            title="On-Time Delivery (OTD)"
            value={otd.isLoading ? '—' : formatPercent(otdValue)}
            subtitle={
              otd.data
                ? `${otd.data.on_time ?? 0} of ${
                    otd.data.total ?? 0
                  } on time`
                : undefined
            }
            icon={CheckCircle2}
            colorClass={otd.isLoading ? 'text-gray-400' : getOTDColor(otdValue)}
            loading={otd.isLoading}
          />
          <KPICard
            title="Avg. Utilization"
            value={
              utilization.isLoading ? '—' : formatPercent(avgUtil)
            }
            subtitle={
              utilization.data?.summary
                ? `${utilization.data.summary.work_centers_analyzed ?? 0} work centers · Bottleneck: ${utilization.data.summary.bottleneck || '—'}`
                : undefined
            }
            icon={Activity}
            colorClass={
              utilization.isLoading
                ? 'text-gray-400'
                : getUtilizationColor(avgUtil)
            }
            loading={utilization.isLoading}
          />
          <KPICard
            title="Avg. Lead Time"
            value={
              leadTime.isLoading
                ? '—'
                : `${Number(avgLeadTime ?? 0).toFixed(1)}h`
            }
            subtitle={
              leadTime.data
                ? `Median ${Number(
                    leadTime.data.median_hours ?? 0
                  ).toFixed(1)}h`
                : undefined
            }
            icon={Clock}
            colorClass={
              leadTime.isLoading
                ? 'text-gray-400'
                : avgLeadTime > 48
                ? 'text-red-600'
                : avgLeadTime > 24
                ? 'text-amber-600'
                : 'text-green-600'
            }
            loading={leadTime.isLoading}
          />
        </div>

        {/* Main Content: Trend + Utilization + Snapshots */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Trend Panel (2 cols) */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                KPI Trend (30 Days)
              </h2>
              <div className="flex gap-1">
                {trendMetrics.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setSelectedMetric(m.key)}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      selectedMetric === m.key
                        ? 'bg-[#45bdd6] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    )}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {trend.isLoading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : trend.data?.data_points && trend.data.data_points.length > 0 ? (
              <div className="space-y-2">
                {/* Simple bar visualization of trend data points */}
                <div className="flex items-end gap-1 h-48">
                  {trend.data.data_points.map(
                    (
                      point: { timestamp: string; value: number },
                      idx: number
                    ) => {
                      const maxVal = Math.max(
                        ...trend.data!.data_points.map(
                          (p: { value: number }) => p.value
                        ),
                        1
                      );
                      const heightPct = (point.value / maxVal) * 100;
                      return (
                        <div
                          key={idx}
                          className="flex-1 flex flex-col items-center gap-1"
                          title={`${point.timestamp}: ${Number(point.value ?? 0).toFixed(1)}`}
                        >
                          <div
                            className="w-full bg-[#45bdd6] rounded-t opacity-80 hover:opacity-100 transition-opacity min-h-[2px]"
                            style={{ height: `${heightPct}%` }}
                          />
                        </div>
                      );
                    }
                  )}
                </div>
                <div className="flex justify-between text-xs text-gray-400">
                  <span>
                    {trend.data.data_points[0]?.timestamp ?? ''}
                  </span>
                  <span className="font-medium text-gray-600">
                    {getKPIMetricLabel(selectedMetric)}
                  </span>
                  <span>
                    {
                      trend.data.data_points[
                        trend.data.data_points.length - 1
                      ]?.timestamp ?? ''
                    }
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <AlertTriangle className="w-8 h-8 mb-2" />
                <p className="text-sm">No trend data available</p>
                <p className="text-xs mt-1">
                  Create snapshots for trend analysis
                </p>
              </div>
            )}
          </div>

          {/* Snapshots Panel (1 col) */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Latest Snapshots
            </h2>
            <SnapshotList
              snapshots={snapshots.data ?? []}
              loading={snapshots.isLoading}
            />
          </div>
        </div>

        {/* Work Center Utilization Breakdown */}
        {utilization.data?.work_centers && utilization.data.work_centers.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Work Center Utilization
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...utilization.data.work_centers]
                .sort(
                  (a, b) =>
                    Number(b.avg_utilization ?? 0) - Number(a.avg_utilization ?? 0)
                )
                .slice(0, 12)
                .map((wc) => (
                  <UtilizationBar
                    key={wc.work_center_id}
                    label={wc.work_center_code || wc.work_center_id}
                    value={Number(wc.avg_utilization ?? 0) * 100}
                  />
                ))}
            </div>
          </div>
        )}

        {/* Error States */}
        {(oee.isError || otd.isError || utilization.isError || leadTime.isError) && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Error loading KPI data
              </p>
              <p className="text-xs text-red-600 mt-1">
                Ensure the backend is running and migrations have been applied.
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
